import React, { useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";

const ClientinoutDetailModal = ({ show, onHide, clientinout }) => {
  // ✅ Hook must always be called first
  const [locationCache, setLocationCache] = useState({});

  if (!clientinout) return null;

  const formatDateTime = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "Asia/Kolkata",
    };
    return new Date(dateString).toLocaleString("en-IN", options);
  };

  // Component to fetch and show live location from coordinates
  const LiveLocation = ({ coordinates }) => {
    const key = `${coordinates.latitude},${coordinates.longitude}`;
    const [address, setAddress] = useState(locationCache[key] || null);

    useEffect(() => {
      if (!coordinates?.latitude || !coordinates?.longitude) {
        setAddress("No coordinates available");
        return;
      }

      const fetchAddress = async () => {
        try {
          const res = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?latlng=${coordinates.latitude},${coordinates.longitude}&key=${process.env.REACT_APP_BASE_API}`,
          );
          const data = await res.json();
          if (data.status === "OK" && data.results.length > 0) {
            setAddress(data.results[0].formatted_address);
            setLocationCache((prev) => ({
              ...prev,
              [key]: data.results[0].formatted_address,
            }));
          } else {
            setAddress("Address not found");
          }
        } catch (err) {
          console.error("Error fetching address:", err);
          setAddress("Error fetching address");
        }
      };

      if (!locationCache[key]) {
        fetchAddress();
      }
    }, [coordinates, key]);

    return <span>{address || "Fetching address..."}</span>;
  };

  const renderTimings = () => {
    if (!Array.isArray(clientinout.vehicleTimings)) return null;

    const relevantTimings = clientinout.vehicleTimings.filter((t) =>
      ["arrived client_location", "left client_location"].includes(t.type),
    );

    return (
      <div className="timeline">
        {relevantTimings.map((timing, index) => {
          const isLast = index === relevantTimings.length - 1;

          let label = "";
          let icon = "";
          switch (timing.type) {
            case "arrived client_location":
              label = "Arrived at Client Location";
              icon = "📍";
              break;
            case "left client_location":
              label = "Left Client Location";
              icon = "🚗";
              break;
            default:
              label = timing.type;
          }

          return (
            <div
              key={index}
              className={`timeline-item ${isLast ? "last-item" : ""}`}
            >
              <div className="timeline-icon">{icon}</div>
              <div className="timeline-content">
                <div className="timeline-label">
                  <b>
                    Step {index + 1}: {label}
                  </b>
                </div>

                <div className="timeline-location">
                  {timing.liveCoordinates ? (
                    <LiveLocation coordinates={timing.liveCoordinates} />
                  ) : (
                    timing.assignedlocation?.[0] || timing.liveLocation || "-"
                  )}
                </div>

                <div className="timeline-details">
                  🕒 {formatDateTime(timing.time)}
                  <br />
                  👤 Client: {timing.clientName || "--"}
                  <br />
                  🔋 Battery: {timing.batteryPercentage ?? "--"}%
                  <br />
                  🚗 KM Driven: {timing.kmDriven ?? "--"}
                </div>
              </div>
              {!isLast && <div className="timeline-connector"></div>}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Vehicle In/Out Details</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <h5>Vehicle Number: {clientinout.numberplate}</h5>
        <div className="client-summary">
          <div className="summary-box">
            <div className="summary-icon">⏱️</div>
            <div className="summary-content">
              <div className="summary-value">
                {clientinout.workingHours ?? 0} hrs
              </div>
              <div className="summary-label">Working Hours</div>
            </div>
          </div>

          <div className="summary-box">
            <div className="summary-icon">🚚</div>
            <div className="summary-content">
              <div className="summary-value">
                {clientinout.totalClientKm ?? 0} km
              </div>
              <div className="summary-label">Client KM Driven</div>
            </div>
          </div>
        </div>

        {renderTimings()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>

      {/* Timeline Styles */}
      <style jsx>{`
        .timeline {
          position: relative;
          padding-left: 30px;
        }
        .timeline-item {
          position: relative;
          padding-bottom: 30px;
        }
        .timeline-item.last-item {
          padding-bottom: 0;
        }
        .timeline-icon {
          position: absolute;
          left: -16px;
          top: 0;
          background-color: #fff;
          color: #4723d9;
          border: 2px solid #4723d9;
          border-radius: 50%;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          z-index: 1;
        }
        .timeline-connector {
          position: absolute;
          left: -1px;
          top: 28px;
          height: calc(100% - 28px);
          width: 2px;
          background-color: #ccc;
        }
        .timeline-content {
          margin-left: 20px;
          padding: 5px 10px;
          background: #f9f9f9;
          border-radius: 6px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }
        .timeline-label {
          font-weight: 600;
          font-size: 1rem;
          margin-bottom: 4px;
        }
        .timeline-location {
          font-size: 0.95em;
          color: #333;
        }
        .timeline-details {
          font-size: 0.85em;
          color: #555;
          margin-top: 4px;
        }
        .client-summary {
          display: flex;
          gap: 15px;
          margin: 15px 0 20px;
        }

        .summary-box {
          flex: 1;
          display: flex;
          align-items: center;
          gap: 12px;
          background: linear-gradient(135deg, #4723d9, #6c63ff);
          color: white;
          border-radius: 12px;
          padding: 14px 18px;
          box-shadow: 0 4px 12px rgba(71, 35, 217, 0.2);
        }

        .summary-icon {
          font-size: 28px;
        }

        .summary-value {
          font-size: 1.3rem;
          font-weight: 700;
          line-height: 1;
        }

        .summary-label {
          font-size: 0.85rem;
          opacity: 0.9;
          margin-top: 3px;
        }
      `}</style>
    </Modal>
  );
};

export default ClientinoutDetailModal;
